import * as coreAxios from "axios";
import { getCookie, setCookie, signout } from "./cookies";
import { baseUrl } from "./constants";
import { setSnackBar } from "../store/common/commonSlice";

let isRefreshing = false;
let refreshSubscribers = [];

const onRefreshed = (accessToken) => {
  refreshSubscribers.forEach((callback) => callback(accessToken));
  refreshSubscribers = [];
};

const addRefreshSubscriber = (callback) => {
  refreshSubscribers.push(callback);
};

export const axios = coreAxios.default.create({
  baseURL: baseUrl,
  headers: {
    common: {
      Authorization: `BEARER ${getCookie("token")}`,
    },
  },
});

const axiosInterceptor = (dispatch) => {
  axios.interceptors.request.use(
    function (config) {
      // Do something before request is sent
      return config;
    },
    function (error) {
      // Do something with request error
      return Promise.reject(error);
    }
  );

  axios.interceptors.response.use(
    function (response) {
      const { method } = response.config;
      // Any status code that lie within the range of 2xx cause this function to trigger
      // Do something with response data
      if (method !== "get") {
        let message = response?.data?.detail || "success";
        let showSnackBar = true;

        if (response.config.url && response.config.url.includes('/user_profile')) {
          message = "Profile updated successfully";
        }
        else if (response.config.url && response.config.url.includes('/job/update/')) {
          message = "Job update success";
        }
        else if (response.config.url && response.config.url.includes('/submit') && (response.config.url.includes('/job/') || response.config.url.includes('process'))) {
          message = "Job create success";
        }
        // User
        else if (response.config.url && (response.config.url.includes('/user'))) {
          if (method === 'post' && response.config.url.includes('/company/') && response.config.url.endsWith('/user')) {
            message = "User created successfully";
          } else if (method === 'put' && response.config.url.includes('/company/user/')) {
            message = "User updated successfully";
          }
        }
        // Assign to Process
        else if (response.config.url && response.config.url.includes('/assign-stage')) {
          showSnackBar = false;
        } else if (response.config.url && response.config.url.includes('/assign_to_stage')) {
          if (method === 'post') message = "Assign to Stage created successfully";
          else if (method === 'put') message = "Assign to Stage updated successfully";
        }
        // Process 
        else if (response.config.url && response.config.url.includes('/process') && !response.config.url.includes('company')) {
          if (method === 'post' && response.config.url.endsWith('/process')) message = "Process created successfully";
          else if (method === 'put') message = "Process updated successfully";
        }
        // Stage
        else if (response.config.url && response.config.url.includes('/stage')) {
          if (method === 'post') message = "Stage created successfully";
          else if (method === 'put') message = "Stage updated successfully";
        }
        // Email Config
        else if (response.config.url && response.config.url.includes('/email-config')) {
          if (method === 'post') message = "Email Configuration created successfully";
          else if (method === 'put') message = "Email Configuration updated successfully";
        }
        // Email Notification
        else if (response.config.url && response.config.url.includes('/email-notification')) {
          if (method === 'post') message = "Email Notification created successfully";
          else if (method === 'put') message = "Email Notification updated successfully";
        }
        // Company
        else if (response.config.url && response.config.url.includes('/company')) {
          if (method === 'post' && response.config.url.endsWith('/company')) {
            message = "Company created successfully";
            message = "Company updated successfully";
          }
        }

        if (showSnackBar) {
          dispatch(
            setSnackBar({
              open: true,
              message: message,
              severity: "success",
            })
          );
        }
      }
      return response.data;
    },
    function (error) {
      let message = error.message;
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          message = detail;
        } else if (Array.isArray(detail)) {
          message = detail.map(err => err.msg || JSON.stringify(err)).join(", ");
        } else if (typeof detail === 'object') {
          message = detail.msg || JSON.stringify(detail);
        }
      }
      // Suppress snackbar for generic Network Errors and 5xx/500/505 server errors
      const isNetworkOr5xx =
        !error.response ||
        (error.response.status >= 500 && error.response.status <= 599) ||
        (typeof message === 'string' && (
          message.toLowerCase().includes("network error") ||
          message.toLowerCase().includes("505") ||
          message.toLowerCase().includes("500")
        ));

      if (!isNetworkOr5xx && message) {
        dispatch(
          setSnackBar({
            open: true,
            message: message,
            severity: "error",
          })
        );
      }
      if (error.response?.status === 403) {
        window.location.replace('/page-403');
      }
      if (error.response?.status === 401) {
        const originalRequest = error.config;
        
        // Ensure we don't loop indefinitely, and we don't intercept the refresh endpoint itself
        if (!originalRequest._retry && !originalRequest.url?.includes('/auth/refresh-token')) {
          originalRequest._retry = true;

          if (!isRefreshing) {
            isRefreshing = true;

            const rToken = getCookie("refresh_token") || "";
            coreAxios.default
              .post(`${baseUrl}/auth/refresh-token?refresh_token=${encodeURIComponent(rToken)}`)
              .then((res) => {
                const { access_token } = res.data;
                setCookie("token", access_token);
                axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
                onRefreshed(access_token);
              })
              .catch((err) => {
                // Refresh token is invalid, expired, or blacklisted
                signout(() => window.location.reload());
              })
              .finally(() => {
                isRefreshing = false;
              });
          }

          // Queue the request
          return new Promise((resolve) => {
            addRefreshSubscriber((newToken) => {
              originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
              resolve(axios(originalRequest)); // Using the configured instance to retry
            });
          });
        }
        
        // If the refresh token endpoint itself returned 401, or it was already retried
        signout(() => window.location.reload());
      }

      return Promise.reject(error);
    }
  );
};

export default axiosInterceptor;
